import {
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import QrCodeIcon from "@mui/icons-material/QrCode";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import type { MouseEventHandler } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { TextEllipsis } from "@/components/TextEllipsis";
import { calculateColumnWidth, getMemberStatus, mapOptional } from "@/utils";
import { formatDate } from "@/formatter";
import { fromISO, now } from "@/lib/dateTime";
import { MemberSort, MemberSortType, MemberStatus } from "@/models";

import { MemberStatusLabel } from "../../components/MemberStatusLabel";

import type { Member } from "@/models";

const columnsWidth = [180, 220, 200, 160, 160, 96, 96].map((item) =>
  calculateColumnWidth(item)
);

export type MemberTableRowProps = {
  data: Member;
};

export function MemberTableRowHeader() {
  return (
    <TableRow>
      <TableCell width={columnsWidth[0]}>
        <SortField field={MemberSort.Code} />
        หมายเลขสมาชิก
      </TableCell>
      <TableCell width={columnsWidth[1]}>ชื่อ</TableCell>
      <TableCell width={columnsWidth[2]} align="center">
        เบอร์โทรศัพท์
      </TableCell>
      <TableCell width={columnsWidth[3]} align="center">
        <SortField field={MemberSort.Memberships} />
        วันสิ้นสุดสมาชิก
      </TableCell>
      <TableCell width={columnsWidth[4]} align="center">
        <SortField field={MemberSort.PersonalTraining} />
        วันสิ้นสุดแพ็กเกจเทรนเนอร์
      </TableCell>
      <TableCell width={columnsWidth[5]}>สถานะ</TableCell>
      <TableCell width={columnsWidth[6]} />
    </TableRow>
  );
}

export function MemberTableRow({ data }: MemberTableRowProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const status = getMemberStatus(data);

  const {
    id: memberId,
    code,
    profile,
    membership,
    personalTraining,
    accountId,
  } = data;

  const id = memberId.toString();

  const { firstName, lastName, phoneNo } = profile;

  const memberBanned = status === MemberStatus.Ban;

  const onClickBan: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation();
    searchParams.set("dialog", "member-ban");
    searchParams.set("id", id);
    setSearchParams(searchParams);
  };

  const onClickVerify: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation();
    searchParams.set("dialog", "member-verify");
    searchParams.set("id", id);
    setSearchParams(searchParams);
  };

  const onClickTableRow: MouseEventHandler<HTMLTableRowElement> = () => {
    navigate(id);
  };

  return (
    <TableRow hover tabIndex={-1} role="link" onClick={onClickTableRow}>
      <TableCell>
        <TextEllipsis variant="body2">{code}</TextEllipsis>
      </TableCell>
      <TableCell>
        <TextEllipsis variant="body2">
          {firstName} {lastName}
        </TextEllipsis>
      </TableCell>
      <TableCell align="center">
        <TextEllipsis variant="body2">{phoneNo ? phoneNo : "-"}</TextEllipsis>
      </TableCell>
      <TableCell align="center">
        <PackageExpiredDateDisplay
          status={status}
          date={membership?.endedAt.toString()}
        />
      </TableCell>
      <TableCell align="center">
        <PackageExpiredDateDisplay
          status={status}
          date={personalTraining?.endedAt.toString()}
        />
      </TableCell>
      <TableCell>
        <MemberStatusLabel variant="body2" status={status} />
      </TableCell>
      <TableCell sx={{ px: 0 }}>
        <Stack direction="row">
          <Tooltip title="แบน">
            <IconButton onClick={onClickBan}>
              <BlockIcon color={memberBanned ? "error" : "inherit"} />
            </IconButton>
          </Tooltip>
          {!accountId && (
            <Tooltip title="ยืนยันสมาชิก">
              <IconButton onClick={onClickVerify}>
                <QrCodeIcon />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
}

export function PackageExpiredDateDisplay({
  status,
  date,
}: {
  status: MemberStatus;
  date?: string | null;
}) {
  const currentTime = now();
  const isExpired = status === MemberStatus.Expired;
  const { text, color, isNearExpired } = mapOptional(date, (iso) => {
    const expiredDate = fromISO(iso);

    const isNearExpired = currentTime.plus({ day: 30 }) > expiredDate;

    const text = isExpired ? "-" : formatDate(expiredDate);

    return {
      text,
      color: isNearExpired && !isExpired ? "error.main" : "",
      isNearExpired,
    };
  }) ?? { text: "-", color: "", isNearExpired: false };

  return (
    <TextEllipsis variant="body2" color={color}>
      {text}
      {isNearExpired && !isExpired && (
        <Typography component="span" variant="inherit">
          <br /> (ใกล้หมดอายุ)
        </Typography>
      )}
    </TextEllipsis>
  );
}

function SortField({ field }: { field: MemberSort }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const sort = searchParams.get("sort") as MemberSort | undefined;
  const sortType = searchParams.get("sortType") as MemberSortType | undefined;
  const setSort = (s: MemberSort) => () => {
    if (s) {
      searchParams.set("sort", s);
      searchParams.set(
        "sortType",
        sortType === MemberSortType.Asc && sort === field
          ? MemberSortType.Desc
          : MemberSortType.Asc
      );
    }

    searchParams.delete("page");
    setSearchParams(searchParams);
  };
  return (
    <IconButton
      sx={{ padding: 0, width: "auto", height: "auto" }}
      onClick={setSort(field)}
    >
      {sort === field && sortType === MemberSortType.Asc ? (
        <ArrowDownwardIcon />
      ) : (
        <ArrowUpwardIcon />
      )}
    </IconButton>
  );
}
